"use client";
import React from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Divider,
} from "@mui/material";
import { motion } from "framer-motion";
import bgImage from "../../assets/media-bg.png";
import Image from "next/image";
import { mediaData } from "./mediaData.js";
import MediaYouTube from "./MediaYouTube";
import MediaInstagram from "./MediaInstagram";
import MediaLinkedIn from "./MediaLinkedin";

const newsTitleSx = {
  fontWeight: 700,
  background:
    "linear-gradient(135deg, #123524 0%, #1f7a4d 55%, #1a6b42 100%)",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  backgroundClip: "text",
};

const MediaPage: React.FC = () => {
  return (
    <Box sx={{ pb: { xs: 10, sm: 2 } }}>
      <Box sx={{ position: "relative", width: "100%", overflow: "hidden" }}>
        <Image
          src={bgImage}
          width={2000}
          height={1000}
          alt="SalonTym media coverage"
          style={{
            objectFit: "cover",
            width: "100%",
            height: "auto",
            display: "block",
          }}
          priority
        />
      <Box
        sx={{
            position: "absolute",
            inset: 0,
          background:
              "linear-gradient(135deg, rgba(18, 53, 36, 0.88) 0%, rgba(31, 122, 77, 0.5) 50%, rgba(18, 53, 36, 0.35) 100%)",
            pointerEvents: "none",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: { xs: 16, sm: 24, md: 32 },
            right: { xs: 16, sm: "auto" },
            transform: "translateY(-50%)",
            maxWidth: { xs: "none", sm: "min(92%, 640px)" },
            mx: { xs: 0, sm: 0 },
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 0 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <Typography
            variant="body1"
              component="p"
            sx={{
              color: "#fff",
                fontWeight: 700,
                fontSize: {
                  xs: "1.15rem",
                  sm: "1.75rem",
                  md: "2.25rem",
                  lg: "3rem",
                },
                lineHeight: 1.25,
                textShadow: "0 2px 24px rgba(0,0,0,0.25)",
              }}
            >
              The media coverage of
              <br />
              <Box component="span" sx={{ color: "#dff6e8" }}>
                Salon
              </Box>
              <Box component="span" sx={{ color: "#f4e8a6" }}>
                Tym
              </Box>
          </Typography>
        </motion.div>
        </Box>
      </Box>
      <Container maxWidth="lg" sx={{ mt: { xs: 3, sm: 4 }, px: { xs: 2, sm: 3 } }}>
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            textAlign="center"
            sx={{ ...newsTitleSx, mb: 2 }}
          >
            Explore our news
          </Typography>
        </motion.div>

        <Grid container spacing={2}>
          {mediaData.map((media, index) => (
            <Grid
              item
              xs={12}
              sm={6}
              md={6}
              key={media.id}
            >
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
              >
                <Card
                  sx={{
                    textAlign: "center",
                    borderRadius: "16px",
                    border: "1px solid rgba(18, 53, 36, 0.1)",
                    boxShadow: "0 8px 28px rgba(18, 53, 36, 0.08)",
                    overflow: "hidden",
                    transition: "box-shadow 0.25s ease, transform 0.25s ease",
                    "&:hover": {
                      boxShadow: "0 14px 40px rgba(18, 53, 36, 0.14)",
                      transform: "translateY(-2px)",
                    },
                  }}
                >
                  <a
                    href={media.link}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <CardMedia
                      component="img"
                      height="200"
                      sx={{
                        objectFit: "contain",
                        maxHeight: "20rem",
                        bgcolor: "rgba(246, 251, 248, 0.6)",
                        transition: "transform 0.5s ease",
                        "&:hover": {
                          transform: "scale(1.03)",
                        },
                      }}
                      image={media.image}
                      alt={media.title}
                    />
                  </a>
                  <CardContent sx={{ py: 2 }}>
                    <Typography
                      variant="h6"
                      component="h2"
                      sx={{
                        fontWeight: 700,
                        textTransform: "uppercase",
                        margin: "0.25rem auto 0.5rem",
                        color: "#123524",
                        fontSize: { xs: "0.95rem", sm: "1.1rem" },
                      }}
                    >
                      {media.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "#5e6d63", lineHeight: 1.6 }}
                    >
                      {media.description}
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>
      <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
        <Divider sx={{ my: { xs: 3, sm: 4 }, borderColor: "rgba(18, 53, 36, 0.12)" }} />
        <MediaYouTube />
        <Divider sx={{ my: { xs: 3, sm: 4 }, borderColor: "rgba(18, 53, 36, 0.12)" }} />
        <MediaInstagram />
        <Divider sx={{ my: { xs: 3, sm: 4 }, borderColor: "rgba(18, 53, 36, 0.12)" }} />
        <MediaLinkedIn />
      </Container>
    </Box>
  );
};

export default MediaPage;
